// ===== Imports Start =====
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiAlertCircle, FiAlertTriangle, FiCheck, FiFileText, FiInbox, FiX } from "react-icons/fi";
import axiosInstance from "../../API/interceptor";
// ===== Imports End =====

// ===== Types Start =====
interface ExportEntry {
  export_timestamp: string;
  file_name: string;
  record_count: number;
  exported_by_name: string;
  exported_by_email: string;
  criteria_display: string;
  status: "completed" | "failed";
  error_message?: string;
}

interface ExportHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}
// ===== Types End =====

// ===== Helpers Start =====
const PAGE_SIZE_OPTIONS = [5, 10, 25];

const formatDate = (timestamp: string) => {
  const d = new Date(timestamp);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString();
};

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};
// ===== Helpers End =====

// ===== ExportHistoryModal Component Start =====
const ExportHistoryModal: React.FC<ExportHistoryModalProps> = ({ isOpen, onClose }) => {
  // ===== State Start =====
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [history, setHistory] = useState<ExportEntry[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // ===== State End =====

  // ===== Fetch on Open Start =====
  useEffect(() => {
    if (!isOpen) return;
    fetchExportHistory();
  }, [isOpen]);
  // ===== Fetch on Open End =====

  // ===== ESC Key Handler Start =====
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen]);
  // ===== ESC Key Handler End =====

  // ===== Fetch Export History Start =====
  const fetchExportHistory = async () => {
    setLoading(true);
    setError(false);
    setHistory([]);
    setCurrentPage(1);
    try {
      const res = await axiosInstance.get("/task_assignment/get_export_history");
      if (res.data.success && Array.isArray(res.data.history)) {
        setHistory(res.data.history);
      } else {
        setHistory([]);
      }
    } catch (err) {
      console.error("Error fetching export history:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  // ===== Fetch Export History End =====

  // ===== Pagination Logic Start =====
  const totalRecords = history.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRecords);
  const pageData = history.slice(startIndex, endIndex);

  const changePage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };
  // ===== Pagination Logic End =====

  if (!isOpen) return null;

  // ===== Render Start =====
  return createPortal(
    <>
      {/* ===== Overlay Start ===== */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-[99998]"
      />
      {/* ===== Overlay End ===== */}

      {/* ===== Offcanvas Start ===== */}
      <div className="fixed top-0 right-0 bottom-0 w-[70vw] bg-white shadow-[-4px_0_24px_rgba(0,0,0,0.15)] z-[99999] flex flex-col translate-x-0 transition-transform duration-300 ease-in-out">
        {/* ===== Modal Header Start ===== */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-[#e9ecef] shrink-0">
          <h5 className="m-0 font-semibold text-base">Export Log</h5>
          <button
            onClick={onClose}
            className="bg-[#17a2b8] border-none rounded text-white text-xl cursor-pointer w-9 h-9 flex items-center justify-center"
          >
            <FiX />
          </button>
        </div>
        {/* ===== Modal Header End ===== */}

        {/* ===== Modal Body Start ===== */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-[300px]">

          {/* Loading */}
          {loading && (
            <div className="text-center py-10">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-[#6c757d] mt-2.5">Loading export history...</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="text-center py-10">
              <FiAlertTriangle size={40} color="#dc3545" />
              <p className="text-[#dc3545] mt-2.5">Failed to load export history</p>
              <small className="text-[#6c757d]">Please try again later</small>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && history.length === 0 && (
            <div className="text-center py-10">
              <FiInbox size={40} color="#adb5bd" />
              <p className="text-[#6c757d] mt-2.5">No export activity found</p>
              <small className="text-[#adb5bd]">Your export history will appear here</small>
            </div>
          )}

          {/* ===== Table Start ===== */}
          {!loading && !error && history.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="bg-[#f8f9fa]">
                    {["#", "File Name", "Export Date", "Records", "Exported By", "Criteria", "Status"].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-left border-b-2 border-[#dee2e6] font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.map((entry, idx) => {
                    const globalIdx = startIndex + idx + 1;
                    const isFailed = entry.status === "failed";
                    const criteria = entry.criteria_display || "No filters applied";
                    const criteriaShort = criteria.length > 60 ? criteria.slice(0, 60) + "…" : criteria;
                    const rowBg = isFailed ? "#fff5f5" : idx % 2 === 0 ? "#fff" : "#f8f9fa";

                    return (
                      <tr
                        key={idx}
                        className="border-b border-[#dee2e6]"
                        style={{ background: rowBg }}
                      >
                        {/* # */}
                        <td className="px-3 py-2.5 text-center">{globalIdx}</td>

                        {/* File Name */}
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <FiFileText color="#6c5ffc" size={16} />
                            <div>
                              <div className="font-medium">{entry.file_name}</div>
                              {entry.error_message && (
                                <small className="text-[#dc3545] flex items-center gap-1">
                                  <FiAlertCircle size={11} /> {entry.error_message}
                                </small>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Export Date */}
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {formatDate(entry.export_timestamp)}
                        </td>

                        {/* Records */}
                        <td className="px-3 py-2.5 text-center">
                          {isFailed ? (
                            <span className="text-[#adb5bd]">--</span>
                          ) : (
                            <>
                              <span className="font-medium">{entry.record_count}</span>{" "}
                              <small className="text-[#6c757d]">records</small>
                            </>
                          )}
                        </td>

                        {/* Exported By */}
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#6c5ffc] text-white flex items-center justify-center text-[11px] font-semibold shrink-0">
                              {getInitials(entry.exported_by_name)}
                            </div>
                            <div>
                              <div className="font-medium">{entry.exported_by_name}</div>
                              <small className="text-[#6c757d]">{entry.exported_by_email}</small>
                            </div>
                          </div>
                        </td>

                        {/* Criteria */}
                        <td className="px-3 py-2.5 max-w-[200px]">
                          <span title={criteria} className={criteria.length > 60 ? "cursor-help" : "cursor-default"}>
                            {criteriaShort}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-2.5 text-center">
                          {isFailed ? (
                            <span className="bg-[#dc3545] text-white rounded px-2 py-[3px] text-xs inline-flex items-center gap-1">
                              <FiX size={11} /> Failed
                            </span>
                          ) : (
                            <span className="bg-[#28a745] text-white rounded px-2 py-[3px] text-xs inline-flex items-center gap-1">
                              <FiCheck size={11} /> Completed
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* ===== Table End ===== */}

        </div>
        {/* ===== Modal Body End ===== */}

        {/* ===== Modal Footer Start ===== */}
        {!loading && !error && history.length > 0 && (
          <div className="border-t border-[#dee2e6] px-5 py-3 flex justify-between items-center shrink-0 flex-wrap gap-2">
            {/* Records info + page size */}
            <div className="flex items-center gap-4">
              <span className="text-[13px] text-[#6c757d]">
                Showing {totalRecords === 0 ? 0 : startIndex + 1} to {endIndex} of {totalRecords} records
              </span>
              <div className="flex items-center gap-1.5">
                <label className="text-[13px] text-[#6c757d] m-0">Rows:</label>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="border border-[#ced4da] rounded px-1.5 py-0.5 text-[13px]"
                >
                  {PAGE_SIZE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex gap-1">
                <button
                  onClick={() => changePage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-2.5 py-1 border border-[#dee2e6] rounded text-[13px] ${
                    currentPage === 1
                      ? "bg-[#f8f9fa] text-[#adb5bd] cursor-not-allowed"
                      : "bg-white text-[#495057] cursor-pointer"
                  }`}
                >
                  Previous
                </button>
                {getPageNumbers().map((p) => (
                  <button
                    key={p}
                    onClick={() => changePage(p)}
                    className={`px-2.5 py-1 border border-[#dee2e6] rounded text-[13px] cursor-pointer ${
                      p === currentPage
                        ? "bg-[#6c5ffc] text-white font-semibold"
                        : "bg-white text-[#495057] font-normal"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => changePage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-2.5 py-1 border border-[#dee2e6] rounded text-[13px] ${
                    currentPage === totalPages
                      ? "bg-[#f8f9fa] text-[#adb5bd] cursor-not-allowed"
                      : "bg-white text-[#495057] cursor-pointer"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        {/* ===== Modal Footer End ===== */}

      </div>
      {/* ===== Offcanvas End ===== */}
    </>,
    document.body
  );
  // ===== Render End =====
};
// ===== ExportHistoryModal Component End =====

export default ExportHistoryModal;